import PropTypes from 'prop-types';
import React, { memo, useState } from 'react';

import LoadingSpinner from 'components/LoadingSpinner';

import {
  ErrorMessage,
  FallbackMessage,
  MapBox,
  RouteContainer,
} from './MapRouteFallBack.styles';

/**
 * RouteMapFallback component that uses iframe implementation
 * This serves as a fallback when the main Map component fails to render
 */
const MapRouteFallback = ({
  origin,
  destination,
  showErrorMessage = true,
  errorMessage = 'Interactive map unavailable. Showing fallback map view.',
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  // Validate coordinates
  if (!origin?.longLat || !destination?.longLat) {
    return (
      <MapBox>
        <ErrorMessage>
          Unable to display map: Invalid location coordinates
        </ErrorMessage>
      </MapBox>
    );
  }

  const mapMode = 'directions';
  const mapUrl =
    `https://www.google.com/maps/embed/v1/${mapMode}?key=${process.env.GOOGLE_MAPS_API_KEY}` +
    `&origin=${origin.longLat[1]},${origin.longLat[0]}` +
    `&destination=${destination.longLat[1]},${destination.longLat[0]}` +
    `&mode=transit`;

  const handleIframeError = () => {
    setIframeError(true);
    setIsLoading(false);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  if (iframeError) {
    return (
      <MapBox>
        <ErrorMessage>
          Map service is currently unavailable. Please try again later.
        </ErrorMessage>
        <RouteContainer>
          <p>Route from:</p>
          <p>
            <strong>
              {origin.address || `${origin.longLat[1]}, ${origin.longLat[0]}`}
            </strong>
          </p>
          <p>to:</p>
          <p>
            <strong>
              {destination.address ||
                `${destination.longLat[1]}, ${destination.longLat[0]}`}
            </strong>
          </p>
        </RouteContainer>
      </MapBox>
    );
  }

  return (
    <MapBox>
      {showErrorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {isLoading && <LoadingSpinner />}
      {!isLoading && (
        <FallbackMessage>
          Fallback Map - Displaying &quot;Arrive By&quot; route information
        </FallbackMessage>
      )}
      <iframe
        id="fallbackLocationMap"
        width={isLoading ? 0 : '100%'}
        height={isLoading ? 0 : 450}
        style={{ border: 0 }}
        loading="lazy"
        title="Fallback Route Map"
        src={mapUrl}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
    </MapBox>
  );
};

MapRouteFallback.propTypes = {
  origin: PropTypes.shape({
    longLat: PropTypes.arrayOf(PropTypes.number).isRequired,
    address: PropTypes.string,
  }).isRequired,
  destination: PropTypes.shape({
    longLat: PropTypes.arrayOf(PropTypes.number).isRequired,
    address: PropTypes.string,
  }).isRequired,
  showErrorMessage: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default memo(MapRouteFallback);
